import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { firstValueFrom, forkJoin, of, Subject, take, takeUntil } from 'rxjs';
import { FileModel } from '../../../models/file.model';
import { LinkCreateModel, LinkModel } from '../../../models/link.model';
import {
  SeminarFilterOptionModel,
  SeminarOnlyNameModel,
} from '../../../models/seminar.model';
import { TagModel, TagSelectorOutput } from '../../../models/tag.model';
import { TrainingCategoryModel } from '../../../models/training-category.model';
import { AlertService } from '../../../services/alert.service';
import { FileManagerStateService } from '../../../services/file-manager-state.service';
import { FileService } from '../../../services/file.service';
import { LinkService } from '../../../services/link.service';
import { TagService } from '../../../services/tag.service';
import { isValidFileType, supportedMimeTypes } from '../../../utils/file.utils';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { SelectOption } from '../custom-select/custom-select.component';
import { Filter } from '../custom-table/custom-table.component';
import {
  EditAssetDialogComponent,
  EditAssetDialogResult,
} from '../edit-asset-dialog/edit-asset-dialog.component';
import { RenameFileModalComponent } from '../rename-file-modal/rename-file-modal.component';
import { TagSelectionDialogComponent } from '../tag-selection-dialog/tag-selection-dialog.component';
import {
  UploadAssetDialogComponent,
  UploadAssetDialogResult,
} from '../upload-asset-dialog/upload-asset-dialog.component';

@Component({
  selector: 'app-file-manager',
  templateUrl: './file-manager.component.html',
  styleUrl: './file-manager.component.scss',
  standalone: false,
})
export class FileManagerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() allowUpload: boolean = true;
  @Input() allowDelete: boolean = true;
  @Input() allowRename: boolean = true;
  @Input() seminarId: number | null = null;
  @Input() seminarCategory: TrainingCategoryModel | null = null;
  @Input() panelCollapsed: boolean = false;
  @Input() isUserLibrary: boolean = false;
  @Input() listView: boolean = false;
  @Input() isAdminView: boolean | null = null;
  @Input() useCategoryColors: boolean = true;
  @Input() enableSeminarFilter: boolean = false;
  @Input() seminarFilterOptions: SeminarFilterOptionModel[] = [];
  @Input() useUploadHubFlow: boolean = false;
  @Input() loadAllAdminIfNoSeminarFilter: boolean = false;

  @Output() filesLoaded: EventEmitter<void> = new EventEmitter<void>();

  private selectedTags: TagModel[] = [];
  public reorderMode = false;
  public isSavingOrder = false;
  public reorderFiles: FileModel[] = [];
  public reorderImageFiles: FileModel[] = [];
  public reorderLinks: LinkModel[] = [];
  public imagePreviewMap: Record<number, string> = {};

  uploadProgress: number | null = null;
  isLoading: boolean = true;

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;

  private destroy$: Subject<void> = new Subject<void>();

  uploadMenuItems: MenuItem[] = [
    {
      label: 'Datei',
      icon: 'fa-regular fa-paperclip-vertical',
      command: () => {
        this.onFileUpload();
      },
    },
    {
      label: 'Link',
      icon: 'fa-regular fa-link',
      command: () => {
        this.onFileUpload(true);
      },
    },
  ];

  filterMenuOpen: boolean;
  tagFilter: Filter = {
    filteredColumn: '',
    type: 'multiselect',
    selectOptions: [],
    selectOptionsLoading: true,
    placeholder: 'Tags laden...',
    label: 'Tags',
  };
  seminarFilter: Filter = {
    filteredColumn: '',
    type: 'seminar',
    selectOptions: [],
    selectOptionsLoading: false,
    placeholder: 'Seminare auswählen...',
    label: 'Seminare',
  };
  filters: Filter[] = [this.tagFilter];

  constructor(
    private fileManagerState: FileManagerStateService,
    private fileService: FileService,
    private linkService: LinkService,
    private alertService: AlertService,
    private dialogService: DialogService,
    private tagService: TagService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    this.filters = this.enableSeminarFilter
      ? [this.tagFilter, this.seminarFilter]
      : [this.tagFilter];
    if (this.enableSeminarFilter) {
      this.seminarFilter.selectOptions = this.seminarFilterOptions;
    }
    await this.refreshData();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['seminarFilterOptions'] && this.enableSeminarFilter) {
      this.seminarFilter.selectOptions = this.seminarFilterOptions;
      this.filters = [this.tagFilter, this.seminarFilter];
    } else if (!this.enableSeminarFilter) {
      this.filters = [this.tagFilter];
    }

    if (
      changes['enableSeminarFilter'] &&
      !changes['enableSeminarFilter'].firstChange
    ) {
      this.filters = this.enableSeminarFilter
        ? [this.tagFilter, this.seminarFilter]
        : [this.tagFilter];
    }

    if (
      changes['useUploadHubFlow'] &&
      !changes['useUploadHubFlow'].firstChange
    ) {
      await this.refreshData();
    }
  }

  public renameFile(file: FileModel): void {
    const dialogRef = this.dialogService.open(RenameFileModalComponent, {
      width: '550px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        currentFileName: file.name,
      },
    });

    dialogRef.onClose.subscribe((newName: string) => {
      if (newName && newName !== file.name) {
        this.fileService
          .renameFile(file.id, newName)
          .pipe(take(1))
          .subscribe({
            next: () => {
              file.name = newName;
              this.alertService.showSuccessAlert('Datei erfolgreich umbenannt');
            },
            error: () => {
              this.alertService.showErrorAlert(
                'Fehler beim Umbenennen der Datei'
              );
            },
          });
      }
    });
  }

  public renameLink(link: LinkModel): void {
    void this.openEditLinkDialog(link);
  }

  public deleteFile(file: FileModel): void {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      width: '475px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        title: 'Datei löschen',
        message:
          'Bist du sicher, dass du diese Datei löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.',
        icon: 'fa-regular fa-trash-can',
        iconColor: 'red',
        confirmButtonText: 'Löschen',
        confirmButtonIcon: 'fa-regular fa-trash-can',
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        const deletion$ = this.seminarId
          ? this.fileService.detachFileFromSeminar(file.id, this.seminarId)
          : this.fileService.deleteFile(file.id);
        deletion$.pipe(take(1)).subscribe({
          next: () => {
            this.alertService.showSuccessAlert('Datei erfolgreich gelöscht');
            this.onFileDeleted(file);
          },
          error: () => {
            this.alertService.showErrorAlert('Fehler beim Löschen der Datei');
          },
        });
      }
    });
  }

  public deleteLink(link: LinkModel): void {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      width: '475px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        title: 'Link löschen',
        message:
          'Bist du sicher, dass du diesen Link löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.',
        icon: 'fa-regular fa-trash-can',
        iconColor: 'red',
        confirmButtonText: 'Löschen',
        confirmButtonIcon: 'fa-regular fa-trash-can',
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        const deletion$ = this.seminarId
          ? this.linkService.detachLinkFromSeminar(link.id, this.seminarId)
          : this.linkService.deleteLink(link.id);
        deletion$.pipe(take(1)).subscribe({
          next: () => {
            this.alertService.showSuccessAlert('Link erfolgreich gelöscht');
            this.onLinkDeleted(link);
          },
          error: () => {
            this.alertService.showErrorAlert('Fehler beim Löschen des Links');
          },
        });
      }
    });
  }

  getDefaultColor(colorType: number): string {
    switch (colorType) {
      case 80:
        if (this.isUserLibrary) {
          return '#E8F0EF';
        }
        return '#d2e0de';
      case 0:
        if (this.isUserLibrary) {
          return '#0E332E';
        }
        return '#1c665b';
      default:
        return '';
    }
  }

  get isFileAndLinkEmpty(): boolean {
    return this.fileManagerState.isFileAndLinkEmpty();
  }

  get hasActiveFilters(): boolean {
    return this.fileManagerState.hasActiveFilters();
  }

  applySearch($event: Event): void {
    const input = $event.target as HTMLInputElement;
    this.fileManagerState.applySearchFilter(input.value);
  }

  get uploadedFilesCount(): number {
    return (
      this.fileManagerState.getFiles().length +
      this.fileManagerState.getLinks().length
    );
  }

  get imageFiles(): FileModel[] {
    return this.fileManagerState
      .getFiles()
      .filter((file: FileModel) => file.type.startsWith('image'));
  }

  get documentFiles(): FileModel[] {
    return this.fileManagerState
      .getFiles()
      .filter((file: FileModel) => file.type.startsWith('application'));
  }

  get links(): LinkModel[] {
    return this.fileManagerState.getLinks();
  }

  async onFileUpload(linkUpload: boolean = false): Promise<void> {
    if (this.useUploadHubFlow) {
      console.log('useUploadHubFlow', linkUpload);
      await this.openUploadAssetDialog(linkUpload ? 'link' : 'file');
      return;
    }

    const tagSelectorOutput = await this.openTagSelectionDialog(linkUpload);

    if (!tagSelectorOutput) return;

    this.selectedTags = tagSelectorOutput.tags;

    if (this.selectedTags.length === 0) {
      this.alertService.showErrorAlert('Bitte wähle mindestens einen Tag aus.');
      return;
    }

    if (linkUpload) {
      await this.uploadLink(
        tagSelectorOutput.link,
        this.selectedTags.map(tag => tag.id),
        this.seminarId ? [this.seminarId] : []
      );
      return;
    }

    this.fileInput.nativeElement.click();
  }

  private openTagSelectionDialog(
    linkUpload: boolean = false,
    title?: string,
    description?: string
  ): Promise<TagSelectorOutput> {
    return new Promise(resolve => {
      const dialogRef = this.dialogService.open(TagSelectionDialogComponent, {
        width: '672px',
        contentStyle: { overflow: 'auto' },
        data: {
          enableLinkInput: linkUpload,
          title,
          description,
        },
      });
      dialogRef.onClose
        .pipe(takeUntil(this.destroy$))
        .subscribe((tagSelectorOutput: TagSelectorOutput) => {
          resolve(tagSelectorOutput);
        });
    });
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      if (!isValidFileType(file)) {
        const fileExtension = file.name.split('.').pop();
        this.alertService.showErrorAlert(
          `Ungültiger Dateityp (${fileExtension}). Bitte lade eine gültige Datei hoch.`
        );
        return;
      }

      await this.uploadFile(
        file,
        this.selectedTags.map(tag => tag.id),
        this.seminarId ? [this.seminarId] : []
      );
    }
  }

  private uploadFile(
    file: File,
    selectedTagIds: number[],
    selectedSeminarIds: number[]
  ): Promise<void> {
    return new Promise(resolve => {
      this.fileService
        .uploadFile(file, selectedTagIds, selectedSeminarIds)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: event => {
            if ('progress' in event) {
              // Update the progress UI
              this.uploadProgress = event.progress;
            }
            if (event.response) {
              // Handle the response
              this.alertService.showSuccessAlert(
                'Datei erfolgreich hochgeladen.'
              );
              const uploadedFile: FileModel = event.response.data;
              this.fileManagerState.addFile(uploadedFile);
              this.uploadProgress = null;
              resolve();
            }
          },
          error: error => {
            if (error.status === 413) {
              this.alertService.showErrorAlert(
                'Datei zu groß. Erlaubte Dateigröße: 100 MB.'
              );
              this.uploadProgress = null;
              return;
            }

            this.alertService.showErrorAlert(
              'Datei konnte nicht hochgeladen werden.'
            );
            this.uploadProgress = null;
          },
        });
    });
  }

  private uploadLink(
    link: LinkCreateModel,
    selectedTagIds: number[],
    selectedSeminarIds: number[]
  ): Promise<void> {
    return new Promise(resolve => {
      this.linkService
        .createLink(link, selectedTagIds, selectedSeminarIds)
        .pipe(take(1))
        .subscribe({
          next: (link: LinkModel) => {
            this.alertService.showSuccessAlert('Link erfolgreich gespeichert.');
            this.fileManagerState.addLink(link);
            resolve();
          },
          error: () => {
            this.alertService.showErrorAlert(
              'Link konnte nicht gespeichert werden.'
            );
          },
        });
    });
  }

  getAcceptedFileTypes(): string {
    const mimeTypes = Object.values(supportedMimeTypes).flat();
    return mimeTypes.join(',');
  }

  onFileDeleted(file: FileModel) {
    this.fileManagerState.removeFile(file.id);
  }

  onLinkDeleted(link: LinkModel) {
    this.fileManagerState.removeLink(link.id);
  }

  onOpenFilterMenu() {
    // Load tags for filter
    this.fetchTagsForFilter();
    this.filterMenuOpen = true;
  }

  private fetchTagsForFilter() {
    this.tagService
      .getAllTags()
      .pipe(take(1))
      .subscribe(tags => {
        this.tagFilter.selectOptions = this.mapTagsToSelectOptions(tags);
        this.tagFilter.selectOptionsLoading = false;
        this.tagFilter.placeholder = 'Tags auswählen...';
      });
  }

  onFiltersChange(filters: Filter[]) {
    this.filters = filters;

    const tagFilterValue = (this.tagFilter.value ?? []) as (string | number)[];

    if (this.enableSeminarFilter && this.useUploadHubFlow) {
      void this.refreshData().then(() => {
        this.fileManagerState.applyTagFilter(tagFilterValue);
      });
      return;
    }
    this.fileManagerState.applyTagFilter(tagFilterValue);
  }

  private mapTagsToSelectOptions(tags: TagModel[]): SelectOption[] {
    return tags.map(tag => ({
      label: tag.name,
      value: tag.id,
    }));
  }

  private normalizePositions<T extends { position?: number }>(items: T[]): T[] {
    items.sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));
    return items.map((item, idx) => ({ ...item, position: idx + 1 }));
  }

  public toggleReorderMode(): void {
    if (!this.reorderMode) {
      // Enter reorder mode: clone and normalize positions for each list
      this.reorderFiles = this.normalizePositions(
        (this.documentFiles ?? []).slice()
      );
      this.reorderImageFiles = this.normalizePositions(
        (this.imageFiles ?? []).slice()
      );
      this.reorderLinks = this.normalizePositions((this.links ?? []).slice());
      this.loadImagePreviews(this.reorderImageFiles);
      this.reorderMode = true;
      return;
    }
    // Leave reorder mode: persist all and exit
    this.saveAllOrders();
  }

  // Called by p-orderList when items are moved
  public onReorderDocuments(): void {
    this.reorderFiles.forEach((f, idx) => (f.position = idx + 1));
    this.reorderFiles = [...this.reorderFiles];
  }
  public onReorderImages(): void {
    this.reorderImageFiles.forEach((f, idx) => (f.position = idx + 1));
    this.reorderImageFiles = [...this.reorderImageFiles];
  }
  public onReorderLinks(): void {
    this.reorderLinks.forEach((l, idx) => (l.position = idx + 1));
    this.reorderLinks = [...this.reorderLinks];
  }

  private saveAllOrders(): void {
    if (!this.seminarId) {
      this.reorderMode = false;
      return;
    }
    this.isSavingOrder = true;

    const filesPayload = [...this.reorderFiles, ...this.reorderImageFiles].map(
      f => ({
        id: f.id,
        position: f.position ?? 0,
      })
    );
    const linksPayload = this.reorderLinks.map(l => ({
      id: l.id,
      position: l.position ?? 0,
    }));

    const calls = [];
    calls.push(
      filesPayload.length > 1
        ? this.fileService
            .updateFileOrder(filesPayload, this.seminarId)
            .pipe(take(1))
        : of(undefined)
    );
    calls.push(
      linksPayload.length > 1
        ? this.linkService
            .reorderLinks(linksPayload, this.seminarId)
            .pipe(take(1))
        : of(undefined)
    );

    forkJoin(calls).subscribe({
      next: () => {
        // Merge reordered files with any other file types that were not part of doc/image sets
        const otherFiles = this.fileManagerState
          .getFiles()
          .filter(
            f =>
              !this.reorderFiles.some(r => r.id === f.id) &&
              !this.reorderImageFiles.some(r => r.id === f.id)
          );

        if (filesPayload.length > 1) {
          this.fileManagerState.setFiles([
            ...this.reorderFiles,
            ...this.reorderImageFiles,
            ...otherFiles,
          ]);
        }
        if (linksPayload.length > 1) {
          this.fileManagerState.setLinks(this.reorderLinks);
        }

        this.isSavingOrder = false;
        this.reorderMode = false;
      },
      error: () => {
        this.isSavingOrder = false;
      },
    });
  }

  private loadImagePreviews(files: FileModel[]): void {
    files
      .filter(f => f.type.startsWith('image/'))
      .forEach(f => {
        if (this.imagePreviewMap[f.id]) return; // already loaded
        this.fileService
          .getFile(f.id)
          .pipe(take(1))
          .subscribe({
            next: blob => {
              const reader = new FileReader();
              reader.onloadend = () => {
                this.imagePreviewMap[f.id] = reader.result as string;
              };
              reader.readAsDataURL(blob);
            },
            error: () => {},
          });
      });
  }

  public get adminAreaActive(): boolean {
    return this.isAdminView !== null
      ? this.isAdminView
      : this.router.url.includes('/administration');
  }

  private async refreshData(): Promise<void> {
    this.isLoading = true;

    if (this.seminarId) {
      await this.fileManagerState.fetchFilesBySeminarId(this.seminarId);
      await this.fileManagerState.fetchLinksBySeminarId(this.seminarId);
    } else if (this.isUserLibrary) {
      await this.fileManagerState.fetchFilesByUser();
      await this.fileManagerState.fetchLinksByUser();
    } else if (this.useUploadHubFlow) {
      const selectedSeminars = (this.seminarFilter.value ?? []) as number[];
      if (selectedSeminars.length > 0) {
        await this.fileManagerState.fetchFilesBySeminars(selectedSeminars);
        await this.fileManagerState.fetchLinksBySeminars(selectedSeminars);
      } else if (this.loadAllAdminIfNoSeminarFilter) {
        await this.fileManagerState.fetchAllFilesAdmin();
        await this.fileManagerState.fetchAllLinksAdmin();
      }
    } else if (this.loadAllAdminIfNoSeminarFilter) {
      await this.fileManagerState.fetchAllFilesAdmin();
      await this.fileManagerState.fetchAllLinksAdmin();
    }

    this.loadImagePreviews(this.imageFiles);
    this.isLoading = false;
    this.filesLoaded.emit();
  }

  private async openUploadAssetDialog(type: 'file' | 'link'): Promise<void> {
    const result = await new Promise<UploadAssetDialogResult | undefined>(
      resolve => {
        const dialogRef = this.dialogService.open(UploadAssetDialogComponent, {
          width: '720px',
          contentStyle: { overflow: 'auto' },
          data: {
            type,
            seminarOptions: this.seminarFilterOptions,
          },
        });
        dialogRef.onClose
          .pipe(take(1))
          .subscribe((dialogResult: UploadAssetDialogResult) =>
            resolve(dialogResult)
          );
      }
    );

    if (!result) return;

    try {
      if (result.type === 'file' && result.file) {
        await this.uploadFile(
          result.file,
          result.tags.map(tag => tag.id),
          result.seminarIds
        );
      }

      if (result.type === 'link' && result.link) {
        await this.uploadLink(
          result.link,
          result.tags.map(tag => tag.id),
          result.seminarIds
        );
      }

      await this.refreshData();
    } catch {
      this.alertService.showErrorAlert(
        'Der Upload konnte nicht vollständig abgeschlossen werden.'
      );
    }
  }

  async openEditLinkDialog(link: LinkModel): Promise<void> {
    const linkDetails = await firstValueFrom(
      this.linkService.getLinkDetails(link.id)
    );

    const dialogRef = this.dialogService.open(EditAssetDialogComponent, {
      width: '720px',
      contentStyle: { overflow: 'auto' },
      data: {
        type: 'link',
        currentName: link.title,
        currentUrl: link.url,
        selectedTags: linkDetails.tags ?? [],
        selectedSeminars: linkDetails.seminars ?? [],
        seminarOptions: this.seminarFilterOptions,
      },
    });
    dialogRef.onClose
      .pipe(take(1))
      .subscribe((dialogResult: EditAssetDialogResult) => {
        if (dialogResult && dialogResult.type === 'link') {
          this.editLinkDetails(link, dialogResult);
        }
      });
  }

  private editLinkDetails(
    link: LinkModel,
    payload: EditAssetDialogResult & { type: 'link' }
  ): void {
    this.linkService
      .updateLinkDetails(link.id, payload)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.refreshData();
          this.alertService.showSuccessAlert('Link erfolgreich bearbeitet');
        },
        error: () => {
          this.alertService.showErrorAlert('Fehler beim Bearbeiten des Links');
        },
      });
  }

  async openEditFileDialog(file: FileModel): Promise<void> {
    const fileDetails = await firstValueFrom(
      this.fileService.getFileDetails(file.id)
    );

    const dialogRef = this.dialogService.open(EditAssetDialogComponent, {
      width: '720px',
      contentStyle: { overflow: 'auto' },
      data: {
        type: 'file',
        currentName: file.name,
        selectedTags: fileDetails.tags ?? [],
        selectedSeminars: fileDetails.seminars ?? [],
        seminarOptions: this.seminarFilterOptions,
      },
    });
    dialogRef.onClose
      .pipe(take(1))
      .subscribe((dialogResult: EditAssetDialogResult) => {
        if (dialogResult) {
          this.editFileDetails(file, dialogResult);
        }
      });
  }

  private editFileDetails(
    file: FileModel,
    payload: EditAssetDialogResult
  ): void {
    this.fileService
      .updateFileDetails(file.id, payload)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.refreshData();
          this.alertService.showSuccessAlert('Datei erfolgreich bearbeitet');
        },
        error: () => {
          this.alertService.showErrorAlert('Fehler beim Bearbeiten der Datei');
        },
      });
  }

  private openEditAssetDialog(data: {
    type: 'file' | 'link';
    currentName: string;
    selectedTags: TagModel[];
    selectedSeminars: SeminarOnlyNameModel[];
    seminarOptions: SeminarFilterOptionModel[];
  }): Promise<EditAssetDialogResult | undefined> {
    return new Promise(resolve => {
      const dialogRef = this.dialogService.open(EditAssetDialogComponent, {
        width: '720px',
        contentStyle: { overflow: 'auto' },
        data,
      });
      dialogRef.onClose
        .pipe(take(1))
        .subscribe((dialogResult: EditAssetDialogResult) =>
          resolve(dialogResult)
        );
    });
  }

  public getIconForFile(file: FileModel): string {
    const name = file.name.toLowerCase();
    const type = (file.type || '').toLowerCase();
    const endsWith = (...exts: string[]) => exts.some(e => name.endsWith(e));
    if (type.includes('pdf') || endsWith('.pdf')) return 'fa-file-pdf';
    if (type.includes('spreadsheet') || endsWith('.xls', '.xlsx', '.csv'))
      return 'fa-file-excel';
    if (type.includes('presentation') || endsWith('.ppt', '.pptx'))
      return 'fa-file-powerpoint';
    if (type.includes('word') || endsWith('.doc', '.docx'))
      return 'fa-file-word';
    if (
      type.startsWith('image/') ||
      endsWith('.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg')
    )
      return 'fa-file-image';
    if (type.includes('zip') || endsWith('.zip', '.rar', '.7z'))
      return 'fa-file-zipper';
    if (type.includes('text') || endsWith('.txt', '.md', '.rtf'))
      return 'fa-file-lines';
    return 'fa-file';
  }

  public getIconForLink(): string {
    return 'fa-link';
  }

  public onOpenFile(file: FileModel): void {
    this.fileService.openFile(file);
  }

  public onDownloadFile(file: FileModel): void {
    this.fileService.downloadFile(file);
  }

  public onOpenLink(link: LinkModel): void {
    window.open(link.url, '_blank', 'noopener');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
